import { message } from '@/components/ui'
import { api } from '@/lib/apiClient'
import { useCallback, useEffect, useMemo, useState } from 'react'

export type BadgePrinterOption = {
  label: string
  value: string
  printerUid?: string | null
  printerId?: string | null
  printerName?: string | null
  printStationPrinterId?: string | null
  printStationId?: string | null
  stationId?: string | null
  stationName?: string | null
  printerProfileId?: string | null
  printerProfileName?: string | null
  rotate180?: boolean | null
  rotationConfigured?: boolean | null
  zplOrientation?: string | null
  isReady?: boolean
}

type DetectPrinterOptions = {
  notifyOnSuccess?: boolean
  notifyWhenEmpty?: boolean
}

type UseBadgePrinterSelectionOptions = {
  enabled?: boolean
  autoDetect?: boolean
  notifyWhenEmpty?: boolean
}

type NormalizedPrintStationSetup = {
  printerOptions: BadgePrinterOption[]
  setupRequired: boolean
  setupMessage?: string
}

const SETUP_REQUIRED_MESSAGE =
  'Create or regenerate a pairing code in Badge Printing settings, then enter the server URL and code in CloudExpo Print Agent.'
const PROFILE_REQUIRED_MESSAGE = 'Select a printer profile for the active station printer.'
const PRINTER_REQUIRED_MESSAGE = 'Select an active printer in CloudExpo Print Agent.'
const STATION_REQUIRED_MESSAGE =
  'Create a print station in Badge Printing settings, then pair CloudExpo Print Agent with its code.'

const printStationSetupEndpoint = (eventSlug: string) =>
  `/events/${eventSlug}/badge_printings/print_agent_setup`

const getErrorMessage = (error: unknown, fallback: string) => {
  if (!(error instanceof Error)) return fallback
  const candidate = error as Error & { data?: { errors?: string[] } }
  return candidate.data?.errors?.join(', ') || error.message || fallback
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const stringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return undefined
}

const firstString = (record: Record<string, unknown>, keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = stringValue(record[key])
    if (value) return value
  }
  return undefined
}

const firstBoolean = (record: Record<string, unknown>, keys: string[]): boolean | undefined => {
  for (const key of keys) {
    if (typeof record[key] === 'boolean') return record[key] as boolean
  }
  return undefined
}

const firstBooleanValue = (record: Record<string, unknown>, keys: string[]): boolean | undefined =>
  firstBoolean(record, keys)

const firstRecord = (
  record: Record<string, unknown>,
  keys: string[]
): Record<string, unknown> | undefined => {
  for (const key of keys) {
    const value = record[key]
    if (isRecord(value)) return value
  }
  return undefined
}

const recordArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.filter(isRecord) : []

const isStationAvailable = (station?: Record<string, unknown>): boolean => {
  if (!station) return true

  const paired = firstBoolean(station, ['paired', 'isPaired'])
  if (paired === false) return false

  const status = firstString(station, ['status'])?.toLowerCase()
  return status !== 'disabled' && status !== 'pairing'
}

const isPrinterAvailable = (printer: Record<string, unknown>): boolean => {
  const active = firstBoolean(printer, ['active', 'isActive', 'enabled'])
  if (active === false) return false

  const status = firstString(printer, ['status'])?.toLowerCase()
  return status !== 'disabled'
}

const printerProfileRecord = (printer: Record<string, unknown>) =>
  firstRecord(printer, ['printer_profile', 'printerProfile', 'profile'])

const normalizePrinterOption = (
  printer: Record<string, unknown>,
  station?: Record<string, unknown>,
  defaultProfile?: Record<string, unknown>,
  disambiguateStationName = false
): BadgePrinterOption | null => {
  const printStationId =
    (station && firstString(station, ['id', 'station_id', 'print_station_id'])) ||
    firstString(printer, ['print_station_id', 'station_id'])
  const stationName =
    (station && firstString(station, ['name', 'display_name', 'label'])) ||
    firstString(printer, ['station_name', 'print_station_name'])
  const printStationPrinterId = firstString(printer, [
    'id',
    'print_station_printer_id',
    'station_printer_id',
  ])
  const printerUid = firstString(printer, ['uid', 'printer_uid', 'value', 'queue_name'])
  const printerName = firstString(printer, [
    'display_name',
    'name',
    'printer_name',
    'label',
    'queue_name',
  ])
  const profile = printerProfileRecord(printer)
  const printerProfileId =
    firstString(printer, ['printer_profile_id', 'profile_id']) ||
    (profile ? firstString(profile, ['id']) : undefined) ||
    (defaultProfile ? firstString(defaultProfile, ['id']) : undefined)
  const resolvedProfile = profile || defaultProfile
  const printerProfileName = resolvedProfile
    ? firstString(resolvedProfile, ['display_name', 'name', 'model'])
    : undefined
  const rotate180 = firstBooleanValue(printer, ['rotate_180', 'rotate180'])
  const rotationConfigured = firstBooleanValue(printer, [
    'rotation_configured',
    'rotationConfigured',
  ])
  const zplOrientation = firstString(printer, ['zpl_orientation', 'zplOrientation'])
  const value =
    (printStationPrinterId && `print_station_printer:${printStationPrinterId}`) ||
    (printStationId && printerUid && `print_station:${printStationId}:printer:${printerUid}`) ||
    printerUid ||
    printerName

  if (!value) return null

  const label =
    disambiguateStationName && stationName && printerName
      ? `${printerName} (${stationName})`
      : printerName || value
  const isReady = Boolean(
    isStationAvailable(station) && isPrinterAvailable(printer) && printerProfileId
  )

  return {
    label,
    value,
    printerUid: printerUid || null,
    printerId: printStationPrinterId || null,
    printerName: printerName || value,
    printStationPrinterId: printStationPrinterId || null,
    printStationId: printStationId || null,
    stationId: printStationId || null,
    stationName: stationName || null,
    printerProfileId: printerProfileId || null,
    printerProfileName: printerProfileName || null,
    rotate180: typeof rotate180 === 'boolean' ? rotate180 : null,
    rotationConfigured: typeof rotationConfigured === 'boolean' ? rotationConfigured : null,
    zplOrientation: zplOrientation || null,
    isReady,
  }
}

const addUniquePrinterOption = (
  unique: Map<string, BadgePrinterOption>,
  option: BadgePrinterOption | null
) => {
  if (!option) return

  const uniqueKey =
    (option.printStationPrinterId && `print_station_printer:${option.printStationPrinterId}`) ||
    (option.printStationId &&
      option.printerUid &&
      `print_station:${option.printStationId}:printer:${option.printerUid}`) ||
    option.value
  const existing = unique.get(uniqueKey)

  if (!existing || (!existing.isReady && option.isReady)) {
    unique.set(uniqueKey, option)
  }
}

const normalizePrintStationSetup = (data: unknown): NormalizedPrintStationSetup => {
  const root = isRecord(data) ? data : {}
  const stations = recordArray(root.stations)
  const printerProfiles = [
    ...recordArray(root.printerProfiles),
    ...recordArray(root.printer_profiles),
  ]
  const defaultProfile =
    firstRecord(root, ['active_printer_profile', 'activePrinterProfile', 'printerProfile']) ||
    printerProfiles[0]
  const unique = new Map<string, BadgePrinterOption>()

  stations.forEach((station) => {
    const stationPrinters = [
      ...recordArray(station.printers),
      ...recordArray(station.printStationPrinters),
      ...recordArray(station.stationPrinters),
    ]
    const activePrinter = firstRecord(station, [
      'active_printer',
      'activePrinter',
      'selected_printer',
      'selectedPrinter',
    ])

    if (activePrinter) {
      stationPrinters.push(activePrinter)
    }

    stationPrinters.forEach((printer) => {
      addUniquePrinterOption(
        unique,
        normalizePrinterOption(printer, station, defaultProfile, stations.length > 1)
      )
    })
  })

  const fallbackStation = firstRecord(root, ['station', 'printStation', 'currentStation'])
  const fallbackPrinters = [
    ...recordArray(root.printers),
    ...recordArray(root.printStationPrinters),
    ...recordArray(root.stationPrinters),
  ]
  const rootActivePrinter = firstRecord(root, [
    'active_printer',
    'activePrinter',
    'selected_printer',
    'selectedPrinter',
  ])

  if (rootActivePrinter) fallbackPrinters.push(rootActivePrinter)

  fallbackPrinters.forEach((printer) => {
    addUniquePrinterOption(unique, normalizePrinterOption(printer, fallbackStation, defaultProfile))
  })

  const printerOptions = Array.from(unique.values())
  const readyOptions = printerOptions.filter((option) => option.isReady)
  const hasStation = stations.length > 0 || Boolean(fallbackStation)
  const hasAvailableStation =
    stations.length > 0
      ? stations.some((station) => isStationAvailable(station))
      : Boolean(fallbackStation && isStationAvailable(fallbackStation))
  const hasPrinter = printerOptions.length > 0
  const hasProfile = printerOptions.some((option) => option.printerProfileId)
  const setupRequired = readyOptions.length === 0

  const setupMessage =
    setupRequired
      ? firstString(root, ['setupMessage', 'message']) ||
        (!hasStation || !hasAvailableStation
          ? STATION_REQUIRED_MESSAGE
          : !hasPrinter
            ? PRINTER_REQUIRED_MESSAGE
            : !hasProfile
              ? PROFILE_REQUIRED_MESSAGE
              : SETUP_REQUIRED_MESSAGE)
      : undefined

  return {
    printerOptions,
    setupRequired,
    setupMessage,
  }
}

async function fetchPrintStationSetup(eventSlug: string): Promise<NormalizedPrintStationSetup> {
  return normalizePrintStationSetup(await api.get<unknown>(printStationSetupEndpoint(eventSlug)))
}

export function useBadgePrinterSelection(
  eventSlug: string,
  {
    enabled = true,
    autoDetect = true,
    notifyWhenEmpty = false,
  }: UseBadgePrinterSelectionOptions = {}
) {
  const [isLoadingPrinters, setIsLoadingPrinters] = useState(false)
  const [printerOptions, setPrinterOptions] = useState<BadgePrinterOption[]>([])
  const [selectedPrinterUid, setSelectedPrinterUid] = useState<string | null>(null)
  const [setupRequired, setSetupRequired] = useState(false)
  const [setupMessage, setSetupMessage] = useState<string | undefined>()

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    const storageKey = `ava-badge-printer:${eventSlug}`
    const stored = window.localStorage.getItem(storageKey)
    if (stored) {
      setSelectedPrinterUid(stored)
    }
  }, [enabled, eventSlug])

  useEffect(() => {
    if (!enabled || !selectedPrinterUid || typeof window === 'undefined') return
    const storageKey = `ava-badge-printer:${eventSlug}`
    window.localStorage.setItem(storageKey, selectedPrinterUid)
  }, [enabled, selectedPrinterUid, eventSlug])

  const detectPrinters = useCallback(
    async ({ notifyOnSuccess = false, notifyWhenEmpty: notifyWhenEmptyOverride }: DetectPrinterOptions = {}) => {
      if (!enabled) return

      const shouldNotifyWhenEmpty =
        typeof notifyWhenEmptyOverride === 'boolean' ? notifyWhenEmptyOverride : notifyWhenEmpty

      setIsLoadingPrinters(true)
      try {
        const setup = await fetchPrintStationSetup(eventSlug)
        setPrinterOptions(setup.printerOptions)
        setSetupRequired(setup.setupRequired)
        setSetupMessage(setup.setupMessage)

        const readyPrinterOptions = setup.printerOptions.filter((option) => option.isReady)

        if (readyPrinterOptions.length === 0) {
          setSelectedPrinterUid(null)
          if (shouldNotifyWhenEmpty) {
            message.warning(setup.setupMessage || SETUP_REQUIRED_MESSAGE)
          }
          return
        }

        setSelectedPrinterUid((prevSelected) => {
          const hasCurrent =
            prevSelected &&
            readyPrinterOptions.some((option) => option.value === prevSelected)
          if (hasCurrent) return prevSelected
          return readyPrinterOptions[0].value
        })

        if (notifyOnSuccess) {
          message.success('Print station refreshed')
        }
      } catch (error: unknown) {
        message.error(getErrorMessage(error, 'Failed to load print station'))
      } finally {
        setIsLoadingPrinters(false)
      }
    },
    [enabled, eventSlug, notifyWhenEmpty]
  )

  useEffect(() => {
    if (!enabled || !autoDetect) return
    detectPrinters()
  }, [enabled, autoDetect, detectPrinters])

  const selectedPrinter = useMemo(
    () => printerOptions.find((option) => option.value === selectedPrinterUid) || null,
    [selectedPrinterUid, printerOptions]
  )

  const hasActivePrinter = useMemo(
    () => Boolean(selectedPrinter?.isReady),
    [selectedPrinter]
  )

  return {
    isLoadingPrinters,
    printerOptions,
    selectedPrinterUid,
    selectedPrinter,
    setSelectedPrinterUid,
    detectPrinters,
    hasActivePrinter,
    setupRequired,
    setupMessage,
  }
}
